/**
 * Writes the token set onto a given element as `--s-*` custom properties.
 * Takes the element as an argument rather than reaching for `document`
 * directly, so this is callable from a test without a real DOM being wired
 * up to anything beyond a plain object with a `style.setProperty` method.
 *
 * Vuetify's own theme system independently emits `--v-*` custom properties
 * from the same token values (see plugins/vuetify.js). The two namespaces
 * do not collide, but both are built from this file's `tokens.js`, which is
 * what keeps them from drifting apart -- see OPT-24-log.md, D1 and C13.
 */
export function applyTokens(el, tokens) {
  const { color, status, tint, tile, tileLabel, radius, typography } = tokens;

  const set = (name, value) => el.style.setProperty(name, value);

  set("--s-color-primary", color.primary);
  set("--s-color-primary-bar", color.primaryBar);
  set("--s-color-accent", color.accent);
  set("--s-color-secondary", color.secondary);
  set("--s-color-money", color.money);
  set("--s-color-ink", color.ink);
  set("--s-color-muted", color.muted);
  set("--s-color-surface", color.surface);
  set("--s-color-card", color.card);
  set("--s-color-border", color.border);

  Object.entries(status).forEach(([key, { fill, text, tint: bg }]) => {
    set(`--s-status-${key}-fill`, fill);
    set(`--s-status-${key}-text`, text);
    set(`--s-status-${key}-tint`, bg);
  });

  Object.entries(tint).forEach(([key, { text, bg }]) => {
    set(`--s-tint-${key}-text`, text);
    set(`--s-tint-${key}-bg`, bg);
  });

  if (tile) {
    Object.entries(tile).forEach(([key, { bg, icon, ink }]) => {
      set(`--s-tile-${key}-bg`, bg);
      set(`--s-tile-${key}-icon`, icon);
      set(`--s-tile-${key}-ink`, ink);
    });
    set("--s-tile-label", tileLabel);
  }

  set("--s-radius-card", radius.card);
  set("--s-radius-pill", radius.pill);
  set("--s-radius-control", radius.control);

  set("--s-font-family", typography.fontFamily);
  set("--s-font-mono", typography.monoFamily);
}

export default applyTokens;
